/**
 * Shared Formatting Utilities
 *
 * Consolidated formatting logic for audit domain to eliminate
 * duplication across components and provide consistent formatting.
 */

/**
 * Timestamp formatting utilities
 */
export function formatTimestamp(timestamp: string | Date, format: 'short' | 'long' | 'relative' = 'short'): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  switch (format) {
    case 'short':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

    case 'long':
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

    case 'relative':
      return formatRelativeTime(date);

    default:
      return date.toLocaleDateString();
  }
}

/**
 * Helper function to format time unit with pluralization
 */
function formatTimeUnit(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? '' : 's'} ago`;
}

/**
 * Calculate time differences for relative formatting
 */
function calculateTimeDifferences(date: Date) {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  return {
    seconds: diffInSeconds,
    minutes: diffInMinutes,
    hours: diffInHours,
    days: diffInDays,
    weeks: Math.floor(diffInDays / 7),
    months: Math.floor(diffInDays / 30),
    years: Math.floor(diffInDays / 365),
  };
}

/**
 * Format relative time (e.g., "2 hours ago", "3 days ago")
 */
export function formatRelativeTime(date: Date): string {
  const diff = calculateTimeDifferences(date);

  if (diff.seconds < 60) return 'Just now';
  if (diff.minutes < 60) return formatTimeUnit(diff.minutes, 'minute');
  if (diff.hours < 24) return formatTimeUnit(diff.hours, 'hour');
  if (diff.days < 7) return formatTimeUnit(diff.days, 'day');
  if (diff.weeks < 4) return formatTimeUnit(diff.weeks, 'week');
  if (diff.months < 12) return formatTimeUnit(diff.months, 'month');

  return formatTimeUnit(diff.years, 'year');
}

/**
 * Format duration in minutes to human-readable format
 */
export function formatDuration(minutes: number): string {
  if (minutes < 1) {
    return '< 1 min';
  }

  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format time range
 */
export function formatTimeRange(startDate: string | Date, endDate: string | Date): string {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  const startFormatted = formatTimestamp(start, 'short');
  const endFormatted = formatTimestamp(end, 'short');

  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Number formatting utilities
 */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat('en-US', options).format(value);
}

export function formatPercentage(value: number, decimals: number = 0): string {
  return `${value.toFixed(decimals)}%`;
}

export function formatCount(count: number): string {
  if (count < 1000) {
    return count.toString();
  }

  if (count < 1000000) {
    return `${(count / 1000).toFixed(1)}K`;
  }

  return `${(count / 1000000).toFixed(1)}M`;
}

/**
 * Text formatting utilities
 */
export function truncateText(text: string, maxLength: number, suffix: string = '...'): string {
  if (text.length <= maxLength) {
    return text;
  }

  return text.substring(0, maxLength - suffix.length) + suffix;
}

export function capitalizeFirst(text: string): string {
  if (!text) return text;

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function formatCamelCase(text: string): string {
  return text
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

/**
 * Status formatting utilities
 */
export function formatStatus(status: string): string {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'Completed';
    case 'pending':
      return 'Pending';
    case 'skipped':
      return 'Skipped';
    case 'active':
      return 'Active';
    case 'in_progress':
      return 'In Progress';
    default:
      return capitalizeFirst(status.replace(/_/g, ' '));
  }
}

/**
 * Content type formatting utilities
 */
export function formatContentType(contentType: string): string {
  const typeMap: Record<string, string> = {
    audio: 'Audio',
    text: 'Text',
    image: 'Image',
    video: 'Video',
    document: 'Document',
  };

  return typeMap[contentType.toLowerCase()] || capitalizeFirst(contentType);
}

/**
 * Language formatting utilities
 */
export function formatLanguage(languageCode: string): string {
  const languageMap: Record<string, string> = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    it: 'Italian',
    pt: 'Portuguese',
    ru: 'Russian',
    ja: 'Japanese',
    ko: 'Korean',
    zh: 'Chinese',
  };

  return languageMap[languageCode.toLowerCase()] || languageCode.toUpperCase();
}

/**
 * File size formatting utilities
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Validation utilities
 */
export function isValidDate(date: string | Date): boolean {
  const d = typeof date === 'string' ? new Date(date) : date;

  return d instanceof Date && !isNaN(d.getTime());
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(email);
}

/**
 * Array formatting utilities
 */
export function formatList(items: string[], conjunction: 'and' | 'or' = 'and'): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;

  const lastItem = items[items.length - 1];
  const otherItems = items.slice(0, -1);

  return `${otherItems.join(', ')}, ${conjunction} ${lastItem}`;
}

export function formatRange(min: number, max: number): string {
  if (min === max) return min.toString();

  return `${min}-${max}`;
}
